import { Fragment, ReactNode, UIEvent, useCallback, useState } from 'react';

import { Popover, useTheme } from '@material-ui/core';

import { FilterViewerComponentProps } from './provider';

interface RenderProps<T> {
  value: T;
  onRemove: () => void;
  onEdit: (e: UIEvent<HTMLElement>) => void;
}

interface FilterViewerProps<T = unknown> extends FilterViewerComponentProps<T> {
  children: (props: RenderProps<T>) => ReactNode;
}

export const FilterViewer = <T,>(props: FilterViewerProps<T>) => {
  const { spacing } = useTheme();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { filter, value, onRemove, onValueChange, children } = props;
  const { editor: EditorComponent } = filter;

  // if the value is empty, remove the filter
  const handleClose = useCallback(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      onRemove();
    }
    setAnchorEl(null);
  }, [value, onRemove]);

  return (
    <Fragment>
      {children({
        value,
        onRemove,
        onEdit: e => setAnchorEl(e.currentTarget),
      })}

      <Popover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: -spacing(1), horizontal: 'left' }}
        PaperProps={{
          elevation: 1,
        }}
      >
        <EditorComponent
          filter={filter}
          value={value}
          onValueChange={onValueChange}
          onClose={() => setAnchorEl(null)}
        />
      </Popover>
    </Fragment>
  );
};
